import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "src/context";
import { useSafeApiCall } from "src/hooks/api/useSafeApiCall";
import { Footer } from "../LoginPage/Footer";
import { Logo } from "src/components/Logo";
import { validEmail } from "src/utils";
import { ForgotPasswordForm } from "./ForgotPasswordForm";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const safeApiCall = useSafeApiCall();
  const navigate = useNavigate();

  const submit = () => {
    const trimmed = email.trim();
    if (!validEmail(trimmed)) {
      setShowError(true);
      return;
    }
    setLoading(true);

    safeApiCall(() => auth.sendPasswordReset(trimmed), {
      toastErrorMsg: "Unable to send reset email.",
      suppressErrorRedirect: true,
    })
      .then(() => {
        setLoading(false);
        void navigate("/login/forgot-password/email-sent");
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const titleText = "Forgot your password";
  const subTitleText = "Enter the email address associated with your account.";
  return (
    <div className="flex flex-col justify-between items-center min-h-screen bg-surface-muted">
      <div className="flex flex-col items-center w-full">
        <div className="grid grid-cols-3 justify-between items-center px-8 mt-8 w-full">
          <Link
            to="/login"
            className="flex gap-2 items-center px-0 py-0 pr-3 group w-fit"
          >
            <div className="flex items-center justify-center size-[34px] bg-[#e5ebfd] rounded-[40px] transition-colors group-hover:bg-[#d4e0fc]">
              <svg
                width="12"
                height="9"
                viewBox="0 0 12 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.66667 0.5C4.66667 0.223858 4.89052 0 5.16667 0H8.16667C10.0997 0 11.6667 1.567 11.6667 3.5C11.6667 5.433 10.0997 7 8.16667 7H1.47518C1.58498 7.10509 1.70475 7.21325 1.8278 7.32009C2.06907 7.52957 2.3118 7.72438 2.49511 7.86744C2.5865 7.93876 2.66252 7.99674 2.71541 8.0367C2.74185 8.05666 2.7945 8.09607 2.80835 8.10639C3.03066 8.27015 3.06634 8.57417 2.9026 8.7965C2.73884 9.01885 2.42575 9.06629 2.2034 8.90253L2.20273 8.90203C2.18662 8.89002 2.14056 8.8557 2.11271 8.83466C2.05623 8.792 1.97599 8.73079 1.87989 8.65579C1.6882 8.5062 1.43093 8.29983 1.17219 8.07519C0.915944 7.8527 0.646809 7.60245 0.437807 7.36591C0.333811 7.24821 0.234125 7.12212 0.157728 6.9954C0.0896045 6.88239 0 6.70513 0 6.5C0 6.29487 0.089608 6.1176 0.157732 6.0046C0.234129 5.87787 0.333815 5.75178 0.437811 5.63409C0.646814 5.39755 0.915947 5.1473 1.1722 4.92481C1.43093 4.70017 1.6882 4.4938 1.87989 4.34421C1.97599 4.26921 2.05623 4.208 2.11271 4.16534C2.14044 4.14439 2.18624 4.11026 2.20252 4.09813L2.2034 4.09747C2.42575 3.93371 2.73884 3.98115 2.9026 4.20349C3.06231 4.42035 3.02107 4.72343 2.81266 4.89019L2.71542 4.9633C2.66252 5.00325 2.58651 5.06124 2.49512 5.13256C2.3118 5.27562 2.06907 5.47043 1.82781 5.67991C1.70475 5.78675 1.58498 5.89491 1.47519 6H8.16667C9.54738 6 10.6667 4.88071 10.6667 3.5C10.6667 2.11929 9.54738 1 8.16667 1H5.16667C4.89052 1 4.66667 0.776142 4.66667 0.5Z"
                  fill="#1453F5"
                />
              </svg>
            </div>
            <span className="text-sm font-medium text-[#31394a]">Back</span>
          </Link>
          <div className="flex justify-center">
            <Logo />
          </div>
          <div></div>
        </div>

        <div className="flex flex-col items-center mx-auto mt-[200px] w-[410px]">
          <div className="flex flex-col gap-4 items-center mb-10 w-full">
            <h1 className="text-[28px] font-semibold leading-[32px] text-on-surface-primary text-center w-[394px]">
              {titleText}
            </h1>
            <p className="text-[13px] font-normal leading-[17px] text-on-surface-muted">
              {subTitleText}
            </p>
          </div>

          <ForgotPasswordForm
            value={email}
            showError={showError}
            onChange={(evt) => setEmail(evt.target.value)}
            onFocus={() => setShowError(false)}
            onBlur={(evt) => {
              const trimmedValue = evt.target.value.trim();
              setEmail(trimmedValue);
              setShowError(!validEmail(trimmedValue));
            }}
            onSubmit={submit}
            loading={loading}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
